import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Quote, ArrowLeft, Sun, Star } from 'lucide-react';
import { formatLocalDate } from '../utils/date';
import FavoriteStar from '../components/FavoriteStar';

/**
 * Daily Reflections archive. Every reflection cycles by day-of-year so past
 * dates are deterministic — we ask the backend for the last N days.
 */
const ReflectionsArchive = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reflections/history`, { params: { days } });
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/40">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-[#2B6B7F] flex items-center gap-1 mb-2"
          data-testid="reflections-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-1 flex items-center gap-2">
            <Sun className="w-8 h-8 text-amber-500" />
            Daily Reflections Archive
          </h1>
          <p className="text-slate-600">
            Missed a day? Every reflection is here — scroll back through the last {days} days.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="reflections-range">
          {[7, 14, 30, 60, 90].map((n) => (
            <Button
              key={n}
              variant={days === n ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(n)}
              className={days === n ? 'bg-[#2B6B7F] hover:bg-[#245565]' : ''}
              data-testid={`reflections-range-${n}`}
            >
              Last {n} days
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-12">Loading reflections…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No reflections found.</p>
        ) : (
          <div className="space-y-4">
            {items.map((r) => (
              <Card
                key={r.date}
                className={r.is_today ? 'border-2 border-amber-300 bg-amber-50/40' : ''}
                data-testid={`reflection-card-${r.date}`}
              >
                <CardHeader className="pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                    {r.is_today ? 'Today · ' : ''}{formatLocalDate(r.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  {r.title && (
                    <CardTitle className="text-lg font-heading text-slate-800">
                      {r.title}
                    </CardTitle>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Quote className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
                    <p className="text-slate-700 leading-relaxed">{r.body}</p>
                  </div>
                  {r.title && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                      <FavoriteStar
                        kind="reflection"
                        itemId={r.title}
                        itemPayload={{ title: r.title, body: r.body, date: r.date }}
                        label="Save"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReflectionsArchive;
